import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import AstrologerCard from './AstrologerCard'
import { decorateAstrologer, skillEmoji } from './astrologersData'
import { useReviewSummaries } from '../../../hooks/useReviews'
import { isExcludedAstrologer } from '../../../utils/excludedAstrologers'
import { Link } from 'react-router-dom'

function SkeletonCard() {
  return (
    <div className="rounded-3xl overflow-hidden animate-pulse" style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.12)' }}>
      <div style={{ height: 272, background: 'rgba(200,146,42,.10)' }} />
      <div className="p-5 space-y-3">
        <div className="h-4 w-2/3 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
        <div className="h-2.5 w-1/2 rounded" style={{ background: 'rgba(200,146,42,.10)' }} />
        <div className="grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 rounded-xl" style={{ background: 'rgba(200,146,42,.08)' }} />
          ))}
        </div>
        <div className="h-8 rounded-xl" style={{ background: 'rgba(200,146,42,.10)' }} />
      </div>
    </div>
  )
}

export default function AstrologersSection() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')

  const {
    data: result,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['astrologersList'],
    // Fetch a margin so excluding Maharaj Ji's account still leaves a full row.
    queryFn: () => api.astrologers.list({ page: 1, per_page: 8 }),
    placeholderData: keepPreviousData,
  })

  const astrologers = useMemo(
    () =>
      (result?.data ?? [])
        .filter((r) => !isExcludedAstrologer(r))
        .slice(0, 4)
        .map(decorateAstrologer),
    [result],
  )

  // Filter tabs come from the primary skills present in the live data.
  const filterTabs = useMemo(() => {
    const skills = [...new Set(astrologers.map((a) => a.category))]
    return [
      { key: 'all', label: 'All' },
      ...skills.map((s) => ({ key: s, label: `${skillEmoji(s)} ${s}` })),
    ]
  }, [astrologers])

  const visible = useMemo(() => {
    const term = search.toLowerCase().trim()
    return astrologers.filter((astro) => {
      const matchesSearch = !term || astro.spec.includes(term)
      const matchesFilter = filter === 'all' || astro.category === filter
      return matchesSearch && matchesFilter
    }).splice(0, 4);
  }, [astrologers, search, filter])

  const liveCount = astrologers.filter((a) => a.status === 'live').length
  const liveRatings = useReviewSummaries('astrologer', visible.map((a) => a.id))

  return (
    <section id="our-astrologers" className="relative py-16 md:py-24 md:px-12 overflow-hidden bg-[#FDFAF6]">
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-32 -right-32 rounded-full" style={{ width: 520, height: 520, background: 'radial-gradient(circle,rgba(245,210,140,.28) 0%,transparent 70%)', filter: 'blur(70px)' }} />
        <div className="absolute -bottom-40 -left-24 rounded-full" style={{ width: 480, height: 480, background: 'radial-gradient(circle,rgba(220,185,120,.22) 0%,transparent 65%)', filter: 'blur(80px)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ width: 700, height: 400, background: 'radial-gradient(ellipse,rgba(253,240,210,.5) 0%,transparent 65%)', filter: 'blur(50px)' }} />
        <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: 'radial-gradient(circle,rgba(200,146,42,.25) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.25),transparent)' }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[min(900px,90vw)] h-px" style={{ background: 'linear-gradient(90deg,transparent,rgba(200,146,42,.18),transparent)' }} />
      </div>

      <div className="max-w-350 mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
          <div className="space-y-5 max-w-2xl">
            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full" style={{ border: '1px solid rgba(200,146,42,.25)', background: 'rgba(200,146,42,.06)' }}>
              <span className="text-xs leading-none" style={{ color: '#C8922A' }}>ॐ</span>
              <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
              <span className="text-[10px] uppercase font-semibold tracking-[.35em] font-cinzel" style={{ color: 'rgba(200,146,42,.8)' }}>05 — Our Astrologers</span>
              <span className="block w-px h-3" style={{ background: 'rgba(200,146,42,.3)' }} />
              <span className="text-xs leading-none" style={{ color: '#C8922A' }}>ॐ</span>
            </div>
            <h2 className="font-cinzel text-3xl sm:text-5xl lg:text-[52px] font-normal tracking-[.03em] leading-[1.15]" style={{ color: '#1C140A' }}>
              Talk to Trusted
              <br />
              <span style={{ background: 'linear-gradient(90deg,#C8922A,#E5A820,#C8922A)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Expert Astrologers
              </span>
            </h2>
            <p className="text-sm sm:text-base font-light leading-relaxed max-w-xl" style={{ color: '#8B7355' }}>
              Verified Vedic experts for Kundli, career, marriage, Vastu and more — available on private video call, 24 × 7.
            </p>
          </div>
          <div className="shrink-0">
            <Link
              to="/consultations"
              className="inline-flex items-center gap-3 font-bold text-[11px] uppercase tracking-[.22em] px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 group"
              style={{ background: '#1C140A', color: '#FFF', boxShadow: '0 8px 32px -8px rgba(28,20,10,.35)' }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#C8922A'; e.currentTarget.style.boxShadow = '0 8px 32px -8px rgba(200,146,42,.5)' }}
              onMouseOut={(e) => { e.currentTarget.style.background = '#1C140A'; e.currentTarget.style.boxShadow = '0 8px 32px -8px rgba(28,20,10,.35)' }}
            >
              Meet All Astrologers
              <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-10">
          <div className="relative w-full sm:w-64 shrink-0">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: '#B5A090' }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or specialty…"
              className="astro-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 flex-1">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setFilter(tab.key)}
                className={`text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border shrink-0 transition-colors${filter === tab.key ? ' tab-w-active' : ''}`}
                style={filter === tab.key ? {} : { borderColor: 'rgba(200,146,42,.22)', color: '#8B7355', background: '#FFF' }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <span className="hidden sm:block text-[10px] font-semibold shrink-0" style={{ color: '#8B7355' }}>
            {visible.length} Astrologer{visible.length !== 1 ? 's' : ''}
          </span>
        </div>

        {isPending ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : isError ? (
          <div className="py-16 text-center">
            <div className="text-5xl mb-4 opacity-40">🛰️</div>
            <p className="font-cinzel text-lg font-bold mb-1" style={{ color: '#1C140A' }}>Couldn’t Load Astrologers</p>
            <p className="text-sm mb-5" style={{ color: '#8B7355' }}>Something went wrong while fetching live data. Please try again.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
            >
              Retry
            </button>
          </div>
        ) : visible.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {visible.map((astro) => (
              <AstrologerCard key={astro.id} astro={astro} liveRating={liveRatings[astro.id]} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <div className="text-5xl mb-4 opacity-40">🔭</div>
            <p className="font-cinzel text-lg font-bold mb-1" style={{ color: '#1C140A' }}>No Astrologers Found</p>
            <p className="text-sm" style={{ color: '#8B7355' }}>Try a different name or specialty filter.</p>
          </div>
        )}

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {astrologers.slice(0, 3).map((a) => (
                <div
                  key={a.id}
                  className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-[10px] font-cinzel font-bold"
                  style={{ background: 'linear-gradient(145deg,#FEF3C7,#FDE68A)', borderColor: 'rgba(200,146,42,.25)', color: '#92400E' }}
                >
                  {a.fallbackLetter}
                </div>
              ))}
              {astrologers.length > 3 && (
                <div className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-[9px] font-bold" style={{ background: '#FFF', borderColor: 'rgba(139,115,85,.2)', color: '#8B7355' }}>
                  +{astrologers.length - 3}
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold tracking-wide" style={{ color: '#1C140A' }}>
                {astrologers.length} Verified Astrologer{astrologers.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#22c55e', boxShadow: '0 0 5px rgba(34,197,94,.6)' }} />
                <p className="text-[9px] uppercase tracking-wider" style={{ color: '#8B7355' }}>
                  {liveCount} available right now
                </p>
              </div>
            </div>
          </div>
          <Link
            to="/consultations"
            href="#our-astrologers"
            className="inline-flex items-center gap-3 border text-[11px] uppercase tracking-[.25em] px-8 py-4 rounded-full transition-all duration-300 group font-semibold"
            style={{ borderColor: 'rgba(200,146,42,.3)', color: '#8B7355' }}
            onMouseOver={(e) => { e.currentTarget.style.borderColor = 'rgba(200,146,42,.6)'; e.currentTarget.style.color = '#C8922A'; e.currentTarget.style.background = 'rgba(200,146,42,.05)' }}
            onMouseOut={(e) => { e.currentTarget.style.borderColor = 'rgba(200,146,42,.3)'; e.currentTarget.style.color = '#8B7355'; e.currentTarget.style.background = 'transparent' }}
          >
            Explore All Astrologers
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  )
}
